import React, { useState } from 'react';
import { Box, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { PictureInPicture2, Play, Timer, Volume2, VolumeX } from 'lucide-react';
import { useFocusTools } from '../contexts/FocusToolsContext';
import PomodoroClock from './PomodoroClock';
import ToolCard, { ToolButton, toolTextSx } from './ToolCard';

const validDuration = (value) => /^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= 180;

export function TimerSoundButton() {
  const { state, act, soundBlocked } = useFocusTools();
  const label = soundBlocked || !state.soundEnabled ? 'Enable timer sound' : 'Mute timer sound';
  return (
    <Tooltip title={label}>
      <IconButton aria-label={label} onClick={() => act('sound', { enabled: soundBlocked || !state.soundEnabled })}
        sx={{ color: state.soundEnabled ? 'primary.main' : 'secondary.main', width: 44, height: 44 }}>
        {state.soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
      </IconButton>
    </Tooltip>
  );
}

export function TimerWindowButton({ iconOnly = false }) {
  const { timerWindow, opening, openTimerWindow } = useFocusTools();
  const label = timerWindow ? 'Show pop-out timer' : 'Pop out timer';
  if (iconOnly) return (
    <Tooltip title={label}><span><IconButton aria-label={label} onClick={openTimerWindow} disabled={opening}
      sx={{ color: 'primary.main', width: 44, height: 44 }}><PictureInPicture2 size={21} /></IconButton></span></Tooltip>
  );
  return <ToolButton fullWidth onClick={openTimerWindow} disabled={opening} startIcon={<PictureInPicture2 size={18} />} aria-label={label}>{label}</ToolButton>;
}

export default function PomodoroTimer() {
  const { state, remainingMs, act, setFocusOpen, windowError } = useFocusTools();
  const [focusInput, setFocusInput] = useState(String(state.focusMinutes));
  const [breakInput, setBreakInput] = useState(String(state.breakMinutes));
  const active = ['running', 'paused'].includes(state.timer.status);
  const focusValid = validDuration(focusInput);
  const breakValid = validDuration(breakInput);
  const durationField = (label, value, setValue, field, valid) => (
    <TextField label={label} type="number" value={value}
      onChange={(event) => { setValue(event.target.value); if (validDuration(event.target.value)) act('minutes', { field, value: Number(event.target.value) }); }}
      inputProps={{ min: 1, max: 180, step: 1, 'aria-label': `${label} minutes` }} error={!valid}
      helperText={valid ? '' : 'Use 1–180 min.'}
      InputLabelProps={{ sx: { fontSize: { xs: '0.65rem', sm: '0.65rem' } } }} FormHelperTextProps={{ sx: toolTextSx }}
      sx={{ flex: 1, minWidth: 0, '& input': { fontSize: '0.85rem' }, '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />
  );
  return (
    <ToolCard id="pomodoro-timer" title="Pomodoro timer" icon={<Timer size={22} />} action={<TimerSoundButton />}>
      <PomodoroClock timer={active ? state.timer : { ...state.timer, phase: 'focus', durationMs: state.focusMinutes * 60000 }}
        remainingMs={active ? remainingMs : state.focusMinutes * 60000} />
      {active ? (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Typography sx={{ ...toolTextSx, color: 'secondary.main', textAlign: 'center' }}>{state.timer.status === 'paused' ? 'Paused' : `${state.timer.phase === 'focus' ? 'Focus' : 'Break'} in progress`}</Typography>
          <ToolButton variant="contained" onClick={() => setFocusOpen(true)}>Open timer</ToolButton>
        </Stack>
      ) : (
        <>
          <Stack direction="row" justifyContent="center" gap={1} sx={{ my: 2 }}>
            {[10, 25, 45].map((minutes) => (
              <ToolButton key={minutes} aria-label={`${minutes} minute focus`} aria-pressed={state.focusMinutes === minutes}
                variant={state.focusMinutes === minutes ? 'contained' : 'outlined'} sx={{ fontSize: '0.6rem', px: 1.5, minWidth: 0 }}
                onClick={() => { setFocusInput(String(minutes)); act('minutes', { field: 'focusMinutes', value: minutes }); }}>{minutes} min</ToolButton>
            ))}
          </Stack>
          <Stack direction="row" gap={2} sx={{ mt: 3, mb: 2.5 }}>
            {durationField('Focus', focusInput, setFocusInput, 'focusMinutes', focusValid)}
            {durationField('Break', breakInput, setBreakInput, 'breakMinutes', breakValid)}
          </Stack>
          {state.timer.status === 'finished' && state.timer.reward && <Typography sx={{ ...toolTextSx, mb: 2, overflowWrap: 'anywhere' }}>Your reward: {state.timer.reward}</Typography>}
          <ToolButton fullWidth variant="contained" startIcon={<Play size={18} />} onClick={() => act('start')} disabled={!focusValid}>Start focus</ToolButton>
          {state.timer.status === 'finished' && state.timer.phase === 'focus' && <Box sx={{ mt: 2 }}><ToolButton fullWidth onClick={() => act('start', { phase: 'break' })} disabled={!breakValid}>Start break</ToolButton></Box>}
        </>
      )}
      <Box sx={{ mt: 2 }}><TimerWindowButton /></Box>
      {windowError && <Typography role="alert" sx={{ ...toolTextSx, mt: 2, color: 'secondary.main' }}>{windowError}</Typography>}
    </ToolCard>
  );
}
